import {
    isSameDay,
    isSameWeek,
    isSameMonth,
    isSameYear,
    subDays,
    subWeeks,
    subMonths,
    userDate,
    startOfDay,
    startOfWeek,
    startOfMonth,
} from 'owa-datetime';
import { formatMonthName, formatYear } from 'owa-datetime-formatters';
import { observableNow } from 'owa-observable-datetime';
import { getUserConfiguration } from 'owa-session-store';
import strings from 'owa-strings/owa-mini-strings';
import { getMailListItemDataProps } from './mailList/getMailListItemDataProps';
import { logUsage } from 'owa-analytics';
import { PREV_ID_NONE } from './constants';

export interface SectionDividerProps {
    prevMailDate: string;
    currentMailDate: string;
}

const cache = new Map();

export const getSectionDivider = (itemId, prevItemId) => {
    if (!prevItemId) {
        return '';
    }

    const itemKey = `${itemId}-${prevItemId}`;
    if (cache.has(itemKey)) {
        return cache.get(itemKey);
    }

    try {
        const currentLastDeliveryTimestamp =
            getMailListItemDataProps(itemId)?.lastDeliveryTimestamp;
        const prevLastDeliveryTimestamp =
            prevItemId !== PREV_ID_NONE // The first item in the mail list can have a grouping of yesterday or older
                ? getMailListItemDataProps(prevItemId)?.lastDeliveryTimestamp
                : '';

        const sectionDividerText = getRelativeDate(
            currentLastDeliveryTimestamp,
            prevLastDeliveryTimestamp
        );

        // Don't cache the entries when strings package is not loaded properly
        if (sectionDividerText !== '') cache.set(itemKey, sectionDividerText);

        return sectionDividerText;
    } catch (error) {
        logUsage('getSectionDividerFailed', {
            error: error.stack?.toString() ?? error.message,
        });
        return '';
    }
};

// Determine which relative day, week, month, or year to show. If the previous
// notification card showed the same then return an empty string (to avoid repeating dividers).
export const getRelativeDate = (currentMailDate: string, prevMailDate: string): string => {
    const today = observableNow();
    const prevDate = prevMailDate.length > 0 ? userDate(prevMailDate) : undefined;
    const mailDate = userDate(currentMailDate);
    const userConfig = getUserConfiguration();
    const userWeekStartDay = userConfig?.UserOptions?.WeekStartDay || 0;

    if (!mailDate) {
        throw new Error('Invalid currentMailDate format');
    }

    if (isSameDay(mailDate, today)) {
        return undefined; // No divider shown for Today
    }

    const yesterday = startOfDay(subDays(today, 1));
    if (isSameDay(mailDate, yesterday)) {
        return prevDate && isSameDay(prevDate, yesterday) ? undefined : strings.yesterdayLabel;
    }

    if (isSameWeek(mailDate, today, userWeekStartDay)) {
        return shouldShowThisWeekLabel(prevDate, today, yesterday)
            ? strings.thisWeekLabel
            : undefined;
    }

    const lastWeek = startOfWeek(subWeeks(today, 1), userWeekStartDay);
    if (isSameWeek(mailDate, lastWeek, userWeekStartDay)) {
        return shouldShowLastWeekLabel(prevDate, yesterday, lastWeek, userWeekStartDay)
            ? strings.lastWeekLabel
            : undefined;
    }

    if (isSameMonth(mailDate, today)) {
        return shouldShowThisMonthLabel(prevDate, today, userWeekStartDay, lastWeek)
            ? strings.thisMonthLabel
            : undefined;
    }

    const lastMonth = startOfMonth(subMonths(today, 1));
    if (isSameMonth(mailDate, lastMonth)) {
        return shouldShowLastMonthLabel(prevDate, today, userWeekStartDay, lastWeek, lastMonth)
            ? strings.lastMonthLabel
            : undefined;
    }

    if (isSameYear(mailDate, today)) {
        return shouldShowMonthNameLabel(prevDate, mailDate) ? formatMonthName(mailDate) : undefined;
    }

    return shouldShowPreviousYearLabel(
        prevDate,
        today,
        yesterday,
        userWeekStartDay,
        lastWeek,
        lastMonth,
        mailDate
    )
        ? formatYear(mailDate)
        : undefined;
};

const shouldShowThisWeekLabel = (prevDate, today, yesterday) => {
    return !prevDate || isSameDay(prevDate, today) || isSameDay(prevDate, yesterday);
};

const shouldShowLastWeekLabel = (prevDate, yesterday, lastWeek, userWeekStartDay) => {
    return (
        !prevDate ||
        isSameDay(prevDate, yesterday) ||
        !isSameWeek(prevDate, lastWeek, userWeekStartDay)
    );
};

const shouldShowThisMonthLabel = (prevDate, today, userWeekStartDay, lastWeek) => {
    return (
        !prevDate ||
        isSameWeek(prevDate, today, userWeekStartDay) ||
        isSameWeek(prevDate, lastWeek, userWeekStartDay)
    );
};

const shouldShowLastMonthLabel = (prevDate, today, userWeekStartDay, lastWeek, lastMonth) => {
    return (
        !prevDate ||
        isSameWeek(prevDate, today, userWeekStartDay) ||
        isSameWeek(prevDate, lastWeek, userWeekStartDay) ||
        !isSameMonth(prevDate, lastMonth)
    );
};

const shouldShowMonthNameLabel = (prevDate, mailDate) => {
    return !prevDate || !isSameMonth(prevDate, mailDate);
};

const shouldShowPreviousYearLabel = (
    prevDate,
    today,
    yesterday,
    userWeekStartDay,
    lastWeek,
    lastMonth,
    mailDate
) => {
    return (
        !prevDate ||
        isSameDay(prevDate, today) ||
        isSameDay(prevDate, yesterday) ||
        isSameWeek(prevDate, today, userWeekStartDay) ||
        isSameWeek(prevDate, lastWeek, userWeekStartDay) ||
        isSameMonth(prevDate, lastMonth) ||
        !isSameYear(prevDate, mailDate)
    );
};
